import { VonageError } from '@/lib/authentication';

// Chrome: "Failed to fetch dynamically imported module: <url>", Firefox:
// "error loading dynamically imported module: <url>", Safari: "Importing a
// module script failed.", Vite's preload helper: "Unable to preload CSS for
// <url>".
const CHUNK_LOAD_SIGNATURE =
  /dynamically imported module|module script failed|unable to preload css/i;

/**
 * Failed lazy chunk/CSS fetch — the running bundle references hashed assets
 * the server no longer has, or the browser cached a bad response under the
 * asset URL. Recoverable client-side with fresh HTML; not a backend outage.
 */
export const isChunkLoadError = (error: unknown): boolean =>
  error instanceof Error && CHUNK_LOAD_SIGNATURE.test(error.message);

/**
 * Classifies an error caught by a top-level boundary as either a backend
 * reachability problem ("server") or a runtime problem inside the app
 * ("client").
 *
 * Server-side signature means the user-visible fallback can legitimately
 * point at the public status page — the user can't recover from it, only
 * Allo can. Client-side means the JS in the app itself misbehaved, and a
 * status page link would mislead.
 *
 * Conservative by design: any error without a clear server-side signature
 * is treated as client. We'd rather miss a 5xx with an unrecognized shape
 * than mislabel a render bug as an outage.
 */
export const isServerError = (error: unknown): boolean => {
  if (!error) {
    return false;
  }

  // VonageError covers token/session/voip failures — these are remote-service
  // reachability problems even when surfaced as a custom error class.
  if (error instanceof VonageError) {
    return true;
  }

  // Chunk-load failures also surface as TypeError "Failed to fetch ...", but
  // they're stale-deploy client errors — pointing at the status page would
  // mislead.
  if (isChunkLoadError(error)) {
    return false;
  }

  // Network-level failures: fetch throws TypeError when the request never
  // reaches the server (offline, DNS, CORS preflight). Safari uses "Load
  // failed", Chromium/Firefox use "Failed to fetch" / "NetworkError".
  if (error instanceof TypeError && /failed to fetch|network|load failed/i.test(error.message)) {
    return true;
  }

  // HTTP responses surfaced as thrown errors. openapi-fetch / fetch wrappers
  // sometimes attach a `status` field; treat 5xx as backend.
  const status = (error as { status?: unknown }).status;
  if (typeof status === 'number' && status >= 500) {
    return true;
  }

  // Internal api-allo wrapper throws `new Error(payload.code ?? 'REQUEST_FAILED')`
  // on a non-ok response. The string is the only signal we have at the
  // boundary — match it explicitly. Other known backend-origin codes can be
  // added here as they surface.
  if (error instanceof Error && error.message === 'REQUEST_FAILED') {
    return true;
  }

  return false;
};
